import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { phrase } from '../lib/i18n/runtime';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** Optional explanatory text announced together with the title. */
  readonly description?: ReactNode;
}

/**
 * Modal dialog: focus moves inside on open, Tab is trapped, Escape closes, and focus returns to the
 * element that opened it.
 */
export function Dialog({ open, title, onClose, children, description }: DialogProps) {
  if (!open) {
    return null;
  }
  return createPortal(
    <DialogPanel title={title} onClose={onClose} description={description}>
      {children}
    </DialogPanel>,
    document.body,
  );
}

function DialogPanel({ title, onClose, children, description }: Omit<DialogProps, 'open'>) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const firstField = panel?.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    (firstField ?? panel)?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || panel === null) {
        return;
      }
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        event.preventDefault();
        panel.focus();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === panel)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused?.isConnected === true && previouslyFocused !== document.body) {
        previouslyFocused.focus();
      } else if (
        document.activeElement === null ||
        document.activeElement === document.body ||
        !document.activeElement.isConnected ||
        panel?.contains(document.activeElement) === true
      ) {
        // The trigger is gone (e.g. "Approve" after approval): keep keyboard users inside the page.
        document.getElementById('main')?.focus({ preventScroll: true });
      }
    };
  }, []);

  return (
    // React events bubble through portals; keep a dialog's form submit from reaching a parent form.
    <div
      className="dialog-backdrop"
      onSubmit={(event) => {
        event.stopPropagation();
      }}
    >
      <div
        ref={panelRef}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description !== undefined ? { 'aria-describedby': descriptionId } : {})}
        tabIndex={-1}
      >
        <h2 id={titleId} className="dialog__title">
          {phrase(title)}
        </h2>
        {description !== undefined && (
          <div id={descriptionId} className="dialog__description">
            {typeof description === 'string' ? phrase(description) : description}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
